import { useState, useEffect } from "react";
import { Plus, RefreshCw, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MenuCategoryTabs } from "@/components/menu/MenuCategoryTabs";
import { MenuItemsList } from "@/components/menu/MenuItemsList";
import { AddMenuItemDialog } from "@/components/menu/AddMenuItemDialog";
import { FloatingCartButton } from "@/components/menu/FloatingCartButton";
import { CartSidePanel } from "@/components/menu/CartSidePanel";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { useStoreManagementStore } from "@/store/storeManagement";
import { useMenuStore } from "@/store/menuStore";
import { useFilteredMenuItems, useMenuCacheUtils, useMenuCategories } from "@/services/menu/menuItem/fetchMenuItems";
import { useMenuCacheManager } from "@/hooks/useMenuCacheManager";
import { useCartStore } from "@/store/cartStore";
import { useWaiterOrderService } from "@/services/orders/waiterOrderService";

const MenuManagement = () => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  
  const {
    selectedStoreId,
    setSelectedStoreId,
    stores,
    isLoading,
  } = useStoreManagementStore();
  const { invalidateMenuCache } = useMenuCacheUtils();
  const { data: categories = [], isLoading: categoriesLoading } = useMenuCategories(selectedStoreId);
  const { toast } = useToast();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { clearCart, getTotalItems, setStore } = useCartStore();
  const { placeOrder } = useWaiterOrderService();
  
  // Initialize cache manager for automatic cache management
  const { lastRefreshTime, clearCache } = useMenuCacheManager({
    storeId: selectedStoreId,
    autoRefreshInterval: 60 * 60 * 1000, // 1 hour
    enableBackgroundRefresh: true,
  });
  
  // Get all menu items for search functionality
  const { data: allMenuItems = [], isLoading: queryLoading, error: queryError } = useFilteredMenuItems(selectedStoreId, {
    categoryId: 'all', // Get all items for search
  });

  // Filter items based on search term and active category
  const filteredItems = allMenuItems.filter((item) => {
    const matchesSearch = !searchTerm || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = !activeCategory || 
      activeCategory === 'all' || 
      item.categoryIds?.includes(activeCategory);
    
    return matchesSearch && matchesCategory;
  });

  // Get categories that have matching items for search results
  const getCategoriesWithResults = () => {
    if (!searchTerm) return categories;
    
    const categoryIdsWithResults = new Set<string>();
    filteredItems.forEach(item => {
      item.categoryIds?.forEach(categoryId => {
        categoryIdsWithResults.add(categoryId);
      });
    });
    
    return categories.filter(category => categoryIdsWithResults.has(category.id));
  };

  // Set active category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory("all");
    }
  }, [categories, activeCategory]);

  // Clear search when changing stores
  useEffect(() => {
    setSearchTerm("");
    setIsSearchActive(false);
  }, [selectedStoreId]);

  // Set store in cart when store changes
  useEffect(() => {
    if (selectedStoreId) {
      setStore(selectedStoreId);
    }
  }, [selectedStoreId, setStore]);

  const handleItemCreated = () => {
    // Invalidate cache to refresh the data
    if (selectedStoreId) {
      invalidateMenuCache(selectedStoreId);
    }

    toast({
      title: t("menuManagement.success"),
      description: t("menuManagement.menuItemCreated"),
    });
  };

  const handlePlaceOrder = async () => {
    const { items, selectedTableId, selectedTableNumber, selectedTableName, getTotalAmount } = useCartStore.getState();
    
    if (!selectedStoreId || !selectedTableId) {
      toast({
        title: t("waiterOrdering.missingInformation"),
        description: t("waiterOrdering.missingInformationDescription"),
        variant: "destructive",
      });
      return;
    }

    const result = await placeOrder({
      storeId: selectedStoreId,
      tableId: selectedTableId,
      tableNumber: selectedTableNumber || '',
      tableName: selectedTableName || '',
      items,
      totalAmount: getTotalAmount(),
    });

    if (result.success) {
      clearCart();
      setIsCartOpen(false);
    }
  };

  const handleRefresh = async () => {
    // Clear cache and refresh data
    clearCache();
    if (selectedStoreId) {
      await invalidateMenuCache(selectedStoreId);
    }
    
    toast({
      title: "Cache cleared",
      description: "Menu items and categories refreshed from database",
    });
  };

  const handleCategoryChange = async (categoryId: string) => {
    // Prevent unnecessary updates if the category is already active
    if (activeCategory === categoryId) {
      return;
    }
    setActiveCategory(categoryId);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setIsSearchActive(!!value);
    
    // If search is cleared, reset to "all" category if no category is active
    if (!value && !activeCategory) {
      setActiveCategory("all");
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setIsSearchActive(false);
    if (!activeCategory) {
      setActiveCategory("all");
    }
  };

  const categoriesToShow = isSearchActive ? getCategoriesWithResults() : categories;
  const hasSearchResults = filteredItems.length > 0;
  const totalItems = allMenuItems.length;
  const searchResultsCount = filteredItems.length;

  return (
    <div className="flex flex-col gap-4 md:gap-6 relative">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-0">
        <h1 className="text-xl md:text-3xl font-bold tracking-tight">
          {t("menuManagement.title")}
        </h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            size={isMobile ? "sm" : "default"}
            className="w-full md:w-auto"
          >
            <RefreshCw className="mr-1 md:mr-2 h-4 w-4" />
            <span className="text-sm md:text-base">Refresh</span>
          </Button>
          <Button
            onClick={() => setIsAddCategoryDialogOpen(true)}
            disabled={!selectedStoreId}
            size={isMobile ? "sm" : "default"}
            className="w-full md:w-auto"
          >
            <Plus className="mr-1 md:mr-2 h-4 w-4" />
            <span className="text-sm md:text-base">
              {t("addCategory")}
            </span>
          </Button>
        </div>
      </div>

      <p className="text-sm md:text-base text-muted-foreground">
        {t("menuManagement.manageYourMenu")}
      </p>

      {/* Search Bar */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search menu items..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-9 pr-9 h-10 md:h-11"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSearch}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* Search Results Summary */}
        {isSearchActive && (
          <div className="mt-2 text-sm text-muted-foreground">
            {hasSearchResults ? (
              <span>
                Found {searchResultsCount} of {totalItems} items
                {activeCategory !== 'all' && categories.length > 0 && (
                  <span> in "{categories.find(c => c.id === activeCategory)?.name || 'Unknown'}"</span>
                )}
              </span>
            ) : (
              <span>No items found matching "{searchTerm}"</span>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-4 md:space-y-6 animate-pulse">
          <div className="h-10 md:h-12 bg-muted rounded-md"></div>
          <div className="h-48 md:h-64 bg-muted rounded-md"></div>
        </div>
      ) : (
        <div className="space-y-4 md:space-y-6">
          <MenuCategoryTabs
            activeCategory={activeCategory}
            setActiveCategory={handleCategoryChange}
            storeId={selectedStoreId}
            onStoreChange={setSelectedStoreId}
            stores={stores}
            categories={categoriesToShow}
            isSearchActive={isSearchActive}
            isAddCategoryDialogOpen={isAddCategoryDialogOpen}
            onAddCategoryDialogOpenChange={setIsAddCategoryDialogOpen}
          />

          {activeCategory ? (
            <MenuItemsList
              categoryId={activeCategory}
              storeId={selectedStoreId}
              searchTerm={searchTerm}
              filteredItems={isSearchActive ? filteredItems : undefined}
            />
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-4 md:p-8 text-center">
              <h3 className="mt-2 text-base md:text-lg font-semibold">
                {t("menuManagement.noCategorySelected")}
              </h3>
              <p className="mb-4 mt-1 text-xs md:text-sm text-muted-foreground">
                {t("menuManagement.selectOrCreateCategory")}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Floating Action Button for Add Menu Item */}
      <Button
        onClick={() => setIsAddDialogOpen(true)}
        disabled={!selectedStoreId || !activeCategory || categories.length === 0}
        size="lg"
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-50 md:bottom-8 md:right-8"
      >
        <Plus className="h-6 w-6" />
        <span className="sr-only">Add Menu Item</span>
      </Button>

      {/* Floating Cart Button */}
      <FloatingCartButton
        onClick={() => setIsCartOpen(true)}
        className="z-60"
      />

      {/* Cart Side Panel */}
      <CartSidePanel
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        onPlaceOrder={handlePlaceOrder}
        storeId={selectedStoreId}
      />

      <AddMenuItemDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onItemCreated={handleItemCreated}
        initialStore={selectedStoreId}
        initialCategoryId={activeCategory}
      />
    </div>
  );
};

export default MenuManagement;
